// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import { X } from "lucide-react";
import { LAYER_CONFIG, DEFAULT_VIEWBOX } from "../../utils/scenarioEditorUtils";

// Scenario Settings Modal
export function ScenarioSettingsModal({
  showScenarioSettings,
  setShowScenarioSettings,
  scenarioMetadata,
  setScenarioMetadata,
  referencePoints,
  elements,
  toggleReferencePoint,
}) {
  if (!showScenarioSettings) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Scenario Settings
          </h3>
          <button
            onClick={() => setShowScenarioSettings(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scenario Name
            </label>
            <input
              type="text"
              value={scenarioMetadata.name}
              onChange={(e) =>
                setScenarioMetadata((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Enter scenario name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={scenarioMetadata.description}
              onChange={(e) =>
                setScenarioMetadata((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm h-20"
              placeholder="Describe your scenario"
            />
          </div>
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">ViewBox Settings</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  X Offset
                </label>
                <input
                  type="number"
                  value={scenarioMetadata.viewBox.x}
                  onChange={(e) =>
                    setScenarioMetadata((prev) => ({
                      ...prev,
                      viewBox: {
                        ...prev.viewBox,
                        x: parseFloat(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Y Offset
                </label>
                <input
                  type="number"
                  value={scenarioMetadata.viewBox.y}
                  onChange={(e) =>
                    setScenarioMetadata((prev) => ({
                      ...prev,
                      viewBox: {
                        ...prev.viewBox,
                        y: parseFloat(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Width
                </label>
                <input
                  type="number"
                  value={scenarioMetadata.viewBox.width}
                  onChange={(e) =>
                    setScenarioMetadata((prev) => ({
                      ...prev,
                      viewBox: {
                        ...prev.viewBox,
                        width:
                          parseFloat(e.target.value) || DEFAULT_VIEWBOX.width,
                      },
                    }))
                  }
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Height
                </label>
                <input
                  type="number"
                  value={scenarioMetadata.viewBox.height}
                  onChange={(e) =>
                    setScenarioMetadata((prev) => ({
                      ...prev,
                      viewBox: {
                        ...prev.viewBox,
                        height:
                          parseFloat(e.target.value) || DEFAULT_VIEWBOX.height,
                      },
                    }))
                  }
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            <button
              onClick={() =>
                setScenarioMetadata((prev) => ({
                  ...prev,
                  viewBox: { ...DEFAULT_VIEWBOX },
                }))
              }
              className="mt-2 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Reset to Default
            </button>
          </div>
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-1">Background Zoom</h4>
            <p className="text-xs text-gray-500 mb-3">
              Scales the background map only — objects and GPS pins stay put.
              Useful when the map can&apos;t be edited and just needs to be
              zoomed in.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0.25"
                max="4"
                step="0.05"
                value={scenarioMetadata.backgroundZoom ?? 1}
                onChange={(e) =>
                  setScenarioMetadata((prev) => ({
                    ...prev,
                    backgroundZoom: parseFloat(e.target.value) || 1,
                  }))
                }
                className="flex-1"
              />
              <input
                type="number"
                min="0.1"
                step="0.05"
                value={scenarioMetadata.backgroundZoom ?? 1}
                onChange={(e) =>
                  setScenarioMetadata((prev) => ({
                    ...prev,
                    backgroundZoom: parseFloat(e.target.value) || 1,
                  }))
                }
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <span className="text-sm text-gray-500 w-12 text-right">
                {Math.round((scenarioMetadata.backgroundZoom ?? 1) * 100)}%
              </span>
            </div>
            <button
              onClick={() =>
                setScenarioMetadata((prev) => ({ ...prev, backgroundZoom: 1 }))
              }
              className="mt-2 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Reset Zoom
            </button>
          </div>
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">
              Coordinate System & GPS Reference
            </h4>
            <div className="space-y-2 mb-4">
              <div className="text-sm text-gray-700">
                <label className="block mb-1 font-medium">
                  Coordinate System:
                </label>
                <select
                  value={scenarioMetadata.coordinateSystem}
                  onChange={(e) =>
                    setScenarioMetadata((prev) => ({
                      ...prev,
                      coordinateSystem: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="svg">SVG (Pixel-based x,y coordinates)</option>
                  <option value="osm">
                    OSM (Geo-based lng,lat coordinates)
                  </option>
                </select>
              </div>
              {scenarioMetadata.coordinateSystem === "osm" && (
                <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
                  <div className="font-medium mb-1">OSM Mode Settings:</div>
                  <div>
                    Center:{" "}
                    {scenarioMetadata.osmCenter
                      ? `${scenarioMetadata.osmCenter.lng.toFixed(6)}, ${scenarioMetadata.osmCenter.lat.toFixed(6)}`
                      : "Not set - Click on map or use GPS pins"}
                  </div>
                  <div>Zoom Level: {scenarioMetadata.osmZoom}</div>
                  <div className="mt-1 text-xs text-gray-600">
                    In OSM mode, polygons store geographic coordinates (lng/lat)
                    instead of pixel coordinates.
                  </div>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-600 mb-2">
              <span className="font-medium">
                Current:{" "}
                {scenarioMetadata.coordinateSystem === "svg"
                  ? "SVG Pixel Coordinates"
                  : "OSM Geo Coordinates"}
              </span>
            </div>
            {referencePoints.length === 0 && (
              <div className="text-sm text-gray-500 italic">
                Add GPS coordinates to 2 pins and mark them as reference points
                to enable GPS scaling.
              </div>
            )}
            {referencePoints.map((refId) => {
              const refPin = elements.find((el) => el.id === refId);
              return refPin ? (
                <div
                  key={refId}
                  className="flex items-center justify-between py-2 border-b"
                >
                  <div>
                    <div className="font-medium text-sm">{refPin.label}</div>
                    {refPin.gps && (
                      <div className="text-xs text-gray-500">
                        {refPin.gps.latitude.toFixed(6)},{" "}
                        {refPin.gps.longitude.toFixed(6)}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => toggleReferencePoint(refId)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : null;
            })}
            {scenarioMetadata.scale && (
              <div className="mt-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                Scale: {scenarioMetadata.scale.toFixed(4)} meters per pixel
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={() => setShowScenarioSettings(false)}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Paste SVG Dialog Modal
export function PasteSVGDialogModal({
  showPasteSVGDialog,
  setShowPasteSVGDialog,
  pasteSVGCode,
  setPasteSVGCode,
  currentLayer,
  handlePasteSVG,
  fitToCanvas,
  setFitToCanvas,
}) {
  if (!showPasteSVGDialog) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Paste SVG Code
          </h3>
          <button
            onClick={() => {
              setShowPasteSVGDialog(false);
              setPasteSVGCode("");
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-700">
              {currentLayer ? (
                <>
                  {React.createElement(LAYER_CONFIG[currentLayer].icon, {
                    className: "w-4 h-4",
                  })}
                  <span className="font-medium">
                    Target Layer: {LAYER_CONFIG[currentLayer].name}
                  </span>
                </>
              ) : (
                <span className="font-medium text-red-600">
                  ⚠️ No layer selected! Please select a layer first.
                </span>
              )}
            </div>
            <div className="text-sm text-blue-600 mt-1">
              All imported elements will be added to this layer
            </div>
          </div>
          {typeof setFitToCanvas === "function" && (
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!fitToCanvas}
                onChange={(e) => setFitToCanvas(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">Fit to canvas area</span> — scale
                and center the imported elements over the background. Turn off
                to keep the original SVG coordinates.
              </span>
            </label>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SVG Code
            </label>
            <textarea
              value={pasteSVGCode}
              onChange={(e) => setPasteSVGCode(e.target.value)}
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
              placeholder={`Paste your SVG code here...\n\nExample:\n\n<svg>\n  <rect x="10" y="10" width="100" height="50" fill="blue" />\n  <circle cx="200" cy="50" r="30" fill="red" />\n  <text x="50" y="100" font-size="16">Hello World</text>\n</svg>`}
              style={{ resize: "vertical" }}
            />
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <h4 className="font-medium text-gray-900 mb-2">
              Supported SVG Elements
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div>• &lt;rect&gt; → Rectangle</div>
              <div>• &lt;circle&gt; → Circle</div>
              <div>• &lt;ellipse&gt; → Circle (averaged)</div>
              <div>• &lt;polygon&gt; → Polygon</div>
              <div>• &lt;polyline&gt; → Polygon</div>
              <div>• &lt;text&gt; → Text</div>
              <div>• &lt;line&gt; → Thin Rectangle</div>
              <div>• &lt;g&gt; → Group (flattened)</div>
              <div>• &lt;path&gt; → Text note (manual conversion needed)</div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Note: Complex paths and advanced features may need manual
              adjustment after import.
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Quick Examples</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() =>
                  setPasteSVGCode(`<svg>
  <rect x="50" y="50" width="100" height="60" fill="#e3f2fd" stroke="#1976d2" stroke-width="2" />
  <circle cx="200" cy="80" r="30" fill="#f3e5f5" stroke="#7b1fa2" stroke-width="2" />
  <text x="120" y="140" font-size="14" fill="#333">Sample Elements</text>
</svg>`)
                }
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                Basic Shapes
              </button>
              <button
                onClick={() =>
                  setPasteSVGCode(`<svg>
  <polygon points="100,50 150,25 200,50 175,100 125,100" fill="#fff3e0" stroke="#f57c00" stroke-width="2" />
  <polyline points="50,120 80,140 110,120 140,140 170,120" fill="none" stroke="#4caf50" stroke-width="3" />
</svg>`)
                }
                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
              >
                Polygons
              </button>
              <button
                onClick={() =>
                  setPasteSVGCode(`<svg>
  <g transform="translate(50,50)">
    <rect x="0" y="0" width="80" height="40" fill="#ffeb3b" stroke="#f57f17" stroke-width="2" />
    <text x="40" y="25" text-anchor="middle" font-size="12" fill="#333">Building</text>
  </g>
</svg>`)
                }
                className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
              >
                Grouped Elements
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={() => {
              setShowPasteSVGDialog(false);
              setPasteSVGCode("");
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePasteSVG}
            disabled={!pasteSVGCode.trim() || !currentLayer}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Import Elements
          </button>
        </div>
      </div>
    </div>
  );
}

// Group Manager Modal
export function GroupManagerModal({
  showGroupManager,
  setShowGroupManager,
  groups,
  selectedGroupId,
  selectGroup,
  editingGroupId,
  setEditingGroupId,
  newGroupName,
  setNewGroupName,
  addGroup,
  updateGroup,
  deleteGroup,
  elements,
}) {
  if (!showGroupManager) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Manage Groups</h3>
          <button
            onClick={() => {
              setShowGroupManager(false);
              setEditingGroupId(null);
              setNewGroupName("");
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          {/* Add New Group */}
          <div className="border-b pb-4">
            <h4 className="font-medium text-gray-900 mb-2">Add New Group</h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Enter group name (e.g., scenario_new_large)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && newGroupName.trim()) {
                    addGroup(newGroupName);
                  }
                }}
              />
              <button
                onClick={() => {
                  if (newGroupName.trim()) {
                    addGroup(newGroupName);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Add
              </button>
            </div>
          </div>

          {/* Group List */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Existing Groups</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {groups
                .filter((g) => g.id !== "layer1")
                .map((group) => (
                  <div
                    key={group.id}
                    className={`flex items-center justify-between p-3 border rounded-lg ${
                      selectedGroupId === group.id
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200"
                    }`}
                  >
                    {editingGroupId === group.id ? (
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          defaultValue={group.name}
                          data-group-id={group.id}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              const input = e.target;
                              updateGroup(group.id, input.value);
                            }
                          }}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            const input = document.querySelector(
                              `input[data-group-id="${group.id}"]`,
                            );
                            if (input) {
                              updateGroup(group.id, input.value);
                            }
                          }}
                          className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingGroupId(null)}
                          className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {group.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {
                              elements.filter((el) => el.groupId === group.id)
                                .length
                            }{" "}
                            polygons
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => selectGroup(group.id)}
                            className={`px-3 py-1 rounded text-sm ${
                              selectedGroupId === group.id
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {selectedGroupId === group.id
                              ? "Selected"
                              : "Select"}
                          </button>
                          <button
                            onClick={() => setEditingGroupId(group.id)}
                            className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-sm hover:bg-yellow-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (
                                // eslint-disable-next-line no-alert
                                window.confirm(
                                  `Delete group "${group.name}"? All polygons in this group will be removed.`,
                                )
                              ) {
                                deleteGroup(group.id);
                              }
                            }}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              {groups.filter((g) => g.id !== "layer1").length === 0 && (
                <div className="text-center text-gray-500 py-4">
                  No groups yet. Create one above.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={() => {
              setShowGroupManager(false);
              setEditingGroupId(null);
              setNewGroupName("");
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Create Group Modal
export function CreateGroupModal({
  showCreateGroupModal,
  setShowCreateGroupModal,
  newGroupNameForModal,
  setNewGroupNameForModal,
  selectedElements,
  createGroupAndAssignElements,
}) {
  if (!showCreateGroupModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Create Group</h3>
          <button
            onClick={() => {
              setShowCreateGroupModal(false);
              setNewGroupNameForModal("");
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group Name
            </label>
            <input
              type="text"
              value={newGroupNameForModal}
              onChange={(e) => setNewGroupNameForModal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newGroupNameForModal.trim()) {
                  createGroupAndAssignElements();
                } else if (e.key === "Escape") {
                  setShowCreateGroupModal(false);
                  setNewGroupNameForModal("");
                }
              }}
              placeholder="Enter group name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="text-sm text-gray-500">
            {selectedElements.length} element
            {selectedElements.length !== 1 ? "s" : ""} will be added to this
            group.
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={() => {
              setShowCreateGroupModal(false);
              setNewGroupNameForModal("");
            }}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={createGroupAndAssignElements}
            disabled={!newGroupNameForModal.trim()}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Create Group
          </button>
        </div>
      </div>
    </div>
  );
}
